import { useEffect, useRef } from "react";
import type { LocalTrack, RemoteTrack, Track } from "livekit-client";
import { Icon } from "./Icons";

/**
 * Renders a single video tile. Attach a `LocalTrack` or `RemoteTrack` and
 * the inner <video> mirrors it. The component also handles the "no track
 * yet" placeholder (gradient with avatar + label) and the "camera off"
 * placeholder (subdued background with name).
 *
 * Audio tracks are not attached here — they go to a hidden top-level <audio>
 * element managed by the InCall screen.
 */
export function VideoTile({
  videoTrack,
  name,
  subtitle,
  isLocal = false,
  cameraOff = false,
  micOff = false,
  placeholder,
  small = false,
  mirror = false,
  fit,
  style,
}: {
  videoTrack?: LocalTrack | RemoteTrack | Track | null;
  name: string;
  subtitle?: string;
  isLocal?: boolean;
  cameraOff?: boolean;
  micOff?: boolean;
  placeholder?: string;
  small?: boolean;
  mirror?: boolean;
  /**
   * How to fit the video inside the tile.
   * - "contain": no cropping, letter/pillar-boxed — better for the main tile
   *   when the remote sends portrait video on a landscape stage.
   * - "cover": crops to fill — better for the self PIP where we want a
   *   tight head-and-shoulders preview.
   * Defaults to "cover" for small tiles (PIP), "contain" for the main tile.
   */
  fit?: "cover" | "contain";
  style?: React.CSSProperties;
}) {
  const ref = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !videoTrack || cameraOff) return;
    // livekit-client exposes attach/detach on Track
    const t = videoTrack as unknown as {
      attach: (el: HTMLVideoElement) => void;
      detach: (el: HTMLVideoElement) => void;
    };
    t.attach(el);
    return () => {
      try {
        t.detach(el);
      } catch {
        // track may already be unpublished
      }
    };
  }, [videoTrack, cameraOff]);

  const hue = hashHue(name);

  const showVideo = !!videoTrack && !cameraOff && !placeholder;

  return (
    <div
      className="video-tile"
      style={{
        position: "relative",
        borderRadius: small ? 12 : 16,
        overflow: "hidden",
        background: cameraOff
          ? `linear-gradient(135deg, oklch(0.22 0.01 ${hue}) 0%, oklch(0.14 0.008 ${hue}) 100%)`
          : `radial-gradient(circle at 30% 30%, oklch(0.42 0.1 ${hue}) 0%, oklch(0.25 0.06 ${hue}) 50%, oklch(0.15 0.04 ${hue}) 100%)`,
        boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.04)",
        display: "flex",
        minHeight: 0,
        minWidth: 0,
        ...style,
      }}
    >
      {showVideo && (
        <video
          ref={ref}
          autoPlay
          playsInline
          muted={isLocal}
          style={{
            width: "100%",
            height: "100%",
            // Default: small (self PIP) → cover, large (main) → contain so
            // portrait video from a phone doesn't get cropped on a landscape
            // recruiter stage.
            objectFit: fit ?? (small ? "cover" : "contain"),
            background: "oklch(0.13 0.008 280)",
            transform: mirror ? "scaleX(-1)" : undefined,
          }}
        />
      )}

      {cameraOff && (
        <div
          style={{
            margin: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
            color: "var(--text-3)",
          }}
        >
          <Avatar name={name} size={small ? 60 : 88} hue={hue} />
          <div
            style={{
              fontSize: small ? 12 : 14,
              color: "var(--text-2)",
            }}
          >
            камера выключена
          </div>
        </div>
      )}

      {!cameraOff && !videoTrack && placeholder && (
        <div
          style={{
            margin: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 10,
            color: "var(--text-3)",
            textAlign: "center",
            padding: 24,
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              border: "1.5px dashed var(--text-4)",
              display: "grid",
              placeItems: "center",
            }}
          >
            <Icon name="person" size={24} />
          </div>
          <div style={{ fontSize: 13, color: "var(--text-2)" }}>
            {placeholder}
          </div>
        </div>
      )}

      {/* Name strip */}
      <div
        style={{
          position: "absolute",
          left: small ? 8 : 14,
          bottom: small ? 8 : 14,
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: small ? "3px 8px" : "5px 10px",
          background: "oklch(0.12 0 0 / 0.55)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderRadius: 999,
          color: "var(--text)",
          fontSize: small ? 11 : 13,
          fontWeight: 500,
          letterSpacing: "-0.005em",
          border: "1px solid oklch(1 0 0 / 0.08)",
        }}
      >
        {micOff && <Icon name="micOff" size={small ? 10 : 12} stroke={2.2} />}
        <span>{isLocal ? `${name} (вы)` : name}</span>
        {subtitle && !isLocal && (
          <span
            style={{
              color: "var(--text-3)",
              fontFamily: "var(--font-mono)",
              fontSize: small ? 9 : 10,
              paddingLeft: 2,
            }}
          >
            · {subtitle}
          </span>
        )}
      </div>
    </div>
  );
}

function Avatar({
  name,
  size = 88,
  hue,
}: {
  name: string;
  size?: number;
  hue: number;
}) {
  const initials = name
    .split(" ")
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase();
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        background: `linear-gradient(135deg, oklch(0.45 0.12 ${hue}) 0%, oklch(0.28 0.08 ${hue}) 100%)`,
        color: `oklch(0.95 0.04 ${hue})`,
        fontFamily: "var(--font-sans)",
        fontWeight: 600,
        fontSize: size * 0.38,
      }}
    >
      {initials}
    </div>
  );
}

function hashHue(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return h % 360;
}
